import React, { useEffect, useState } from 'react';
import { Check, Copy, Eye, EyeOff, UserCog, Wand2, X } from 'lucide-react';
import { Icon } from './Icon';
import Button from './ui/Button';
import Input from './ui/Input';
import { Z } from '../constants/tokens';

export default function ProfileModal({ theme, currentUser, axiosAuth, onClose, onUpdated }) {
  const isGoogleAuth = currentUser?.auth_provider === 'google';
  const [displayName, setDisplayName] = useState(currentUser.display_name || currentUser.name || '');
  const [email,       setEmail]       = useState(currentUser.email || '');
  const [newPw,       setNewPw]       = useState('');
  const [showPw,      setShowPw]      = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [msg,         setMsg]         = useState('');
  const [err,         setErr]         = useState('');
  const [copied,      setCopied]      = useState(false);

  const clearBanners = () => { setMsg(''); setErr(''); };

  // Escape closes the modal (keyboard users otherwise have to tab to X / Cancel)
  useEffect(() => {
    const onKeyDown = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(newPw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setErr('Could not copy to clipboard.');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*';
    let pw = '';
    const arr = new Uint8Array(16);
    window.crypto.getRandomValues(arr);
    arr.forEach(b => { pw += chars[b % chars.length]; });
    setNewPw(pw);
    setShowPw(true);
  };

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const body = { display_name: displayName, email };
      if (newPw) {
        if (newPw.length < 8) { setErr('Password must be at least 8 characters.'); setSaving(false); return; }
        body.new_password = newPw;
      }
      const { data } = await axiosAuth.put('/auth/profile', body);
      onUpdated({ display_name: data.display_name, email: data.email, name: data.display_name });
      setMsg('Profile updated!');
      setNewPw('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: theme.textSub, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: Z.modal, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}>
      <div role="dialog" aria-modal="true" aria-label="Edit Profile" style={{ width: 'min(400px, 92vw)', background: theme.sidebar, border: `2px solid ${theme.border}`, borderRadius: '14px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon icon={UserCog} size={16} color={theme.glossaryAccent} />Edit Profile
          </div>
          <Button theme={theme} variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close" title="Close" />
        </div>

        {msg && <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '0.82rem', color: '#166534' }}>{msg}</div>}
        {err && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '0.82rem', color: '#b91c1c' }}>{err}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Display Name</label>
            <Input theme={theme} size="lg" autoFocus value={displayName} onChange={e => { clearBanners(); setDisplayName(e.target.value); }} placeholder="Your name" />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <Input theme={theme} size="lg" type="email" value={email} onChange={e => { clearBanners(); setEmail(e.target.value); }} placeholder="you@example.com" />
          </div>
          {!isGoogleAuth && <div>
            <label style={labelStyle}>New Password <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(leave blank to keep current)</span></label>
            <div style={{ position: 'relative' }}>
              <Input theme={theme} size="lg" type={showPw ? 'text' : 'password'} value={newPw} onChange={e => { clearBanners(); setNewPw(e.target.value); }}
                style={{ paddingRight: '92px' }} placeholder="Min. 8 characters" />
              <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button type="button" onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'} aria-pressed={showPw} title={showPw ? 'Hide password' : 'Show password'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, display: 'flex', padding: '2px' }}>
                  <Icon icon={showPw ? EyeOff : Eye} size={14} />
                </button>
                <button type="button" onClick={generatePassword}
                  title="Generate secure password"
                  style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '5px', cursor: 'pointer', color: theme.textSub, fontSize: '0.65rem', fontWeight: 'bold', padding: '3px 6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Icon icon={Wand2} size={12} /> Gen
                </button>
              </div>
            </div>
            {newPw && showPw && (
              <div style={{ marginTop: '5px', fontSize: '0.72rem', color: theme.textSub, fontFamily: 'monospace', wordBreak: 'break-all', padding: '5px 8px', background: theme.card, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span>{newPw}</span>
                <button onClick={copyPw} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? theme.success : theme.glossaryAccent, fontSize: '0.65rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}><Icon icon={copied ? Check : Copy} size={12} />{copied ? 'Copied' : 'Copy'}</button>
              </div>
            )}
          </div>}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
          <Button theme={theme} variant="secondary" size="lg" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button theme={theme} variant="primary" size="lg" onClick={save} loading={saving} icon={Check} style={{ flex: 2 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
